/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";

import { FormChildProps } from "@/v1/components/atoms/FormChild";
import { RegisterPropsTypes, useFormContext } from "@/v2/context/FormContext";
import { FormEvents } from "@/v2/context/FormContext/utils/constants";
import { errorChangeEvent } from "@/v2/context/FormContext/utils/errorChangeEvent";
import { valueSetEvent } from "@/v2/context/FormContext/utils/valueSetEvent";

import { useForceUpdate } from "./useForceUpdate";

interface UseFormChildProps {
  childProps: FormChildProps;
  type?: RegisterPropsTypes;
}

export const useFormChild = ({ childProps, type }: UseFormChildProps) => {
  const { subscribe, register, getValue } = useFormContext();
  const { name, errors, defaultValue, defaultChecked, value } = childProps;
  const { onChange } = useMemo(
    () => register({ name, errors, defaultValue, defaultChecked, type, value }),
    [register],
  );
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [forceUpdate, forceUpdateValue] = useForceUpdate();
  const currentValue = getValue(name);

  useEffect(() => {
    const unsubscribeValue = subscribe(valueSetEvent(name), forceUpdate);
    const unsubscribeError = subscribe(errorChangeEvent(name), setErrorMessage);
    const unsubscribeResetForm = subscribe(FormEvents.RESET_FORM, () => {
      setErrorMessage(null);
      forceUpdate();
    });

    return () => {
      unsubscribeValue();
      unsubscribeError();
      unsubscribeResetForm();
    };
  }, []);

  return {
    onChange,
    errorMessage,
    value: currentValue,
    required: Boolean(childProps.errors?.required),
    hasError: !!errorMessage,
    forceUpdateValue,
  };
};
